import React from 'react';
import PropTypes from 'prop-types';
import {
  setTrk,
  TrkTag,
  TrkElement,
} from '@nerdwallet/nw-client-lib/trk-helper';
import uuid from 'uuid/v4';

import { ElementImpression } from '@nerdwallet/nw-client-lib/tracking';
import { Impression } from '@nerdwallet/analytics/react/impression';

import classNames from 'classnames';
import classes from '@nerdwallet/base-styles/classes';

import { Text, Title, Box } from '@nerdwallet/currency';

import {
  stringInputSpec,
  stringSelectSpec,
  boolCheckboxSpec,
} from '../../lib/editing-configs';
import classColor from '../../lib/parse-color';
import getTextColor from '../../lib/text-color-config';

import './box-table.less';

const headingSizesEnum = {
  1: 'small',
  2: 'default',
  3: 'large',
  4: 'default',
  5: 'large',
};

class BoxTableBox extends React.Component {
  constructor() {
    super();
    this.redirectAfterNewTab = this.redirectAfterNewTab.bind(this);

    const nodeId = uuid();

    this.state = {
      trkId: nodeId,
    };
  }

  redirectAfterNewTab() {
    setTimeout(() => {
      window.location.href = this.props.redirectUrl;
    }, 1);
  }

  render() {
    const {
      type,
      linkUrl,
      highlight,
      title,
      headingStyle,
      subtitle,
      shadow,
      textColor,
      newTab,
      redirectUrl,
      use2021Layout,
      textColorOptions,
      layout,
      shouldTrack,
      iconUrl,
    } = this.props;
    const borderStyle = shadow ? 'shadow-1' : '';
    const { trkId } = this.state;
    const elementImpressionProps = {
      id: `box-table-${trkId}`,
      elementName: 'BoxTable',
    };

    const isAnchorLink = (linkUrl || '').charAt(0) === '#';
    const modifiedUrl = isAnchorLink
      ? linkUrl
      : linkUrl &&
        setTrk({
          url: linkUrl,
          params: {
            [TrkTag.location]: 'BoxTable',
            [TrkTag.element]: TrkElement.hyperlink,
            [TrkTag.copy]: title?.toLowerCase(),
            [TrkTag.sectionCategory]: 'AwardsNavigation--header',
          },
        });

    let TextComponent = Text;

    if (headingStyle === '4' || headingStyle === '5') {
      TextComponent = Title;
    }

    const hoverStyle = use2021Layout
      ? 'hover-background-use-boa-layout'
      : 'hover-background';
    let textStyleOptions = textColorOptions;
    if (textColor && !textColorOptions) {
      textStyleOptions = { textColor };
    }
    const { textColor: textStyle } = getTextColor(textStyleOptions);
    const colorStyle = { color: textStyle };
    const url = shouldTrack ? modifiedUrl : linkUrl;

    const boxOutput = (
      <a
        href={url}
        target={newTab && '_blank'}
        onClick={newTab && redirectUrl && this.redirectAfterNewTab}
      >
        <Box
          className={classNames([
            'box-table__content',
            layout,
            classes('padding-vertical-4'),
            classes('display-flex'),
            classes('flex-1'),
            classes('align-content-center'),
            classes('justify-content-center'),
            classes('align-items-center'),
            classes('flex-direction-column'),
          ])}
        >
          {iconUrl && (
            <img className="box-table-icon" src={iconUrl} alt="box-icon" />
          )}
          {highlight && highlight !== '' && (
            <Box
              className={classNames([
                'box-table__highlight-cont',
                classes('display-inline-block'),
                classes('margin-bottom-1'),
                classes('background-green'),
              ])}
            >
              <Text
                small
                className={classNames([
                  'box-table__highlight',
                  classes('color-white'),
                  classes('padding-horizontal-2'),
                  classes('padding-vertical-1'),
                  classes('bold'),
                  classes('uppercase'),
                  classes('text-align-center'),
                ])}
              >
                {highlight}
              </Text>
            </Box>
          )}
          <Box
            style={colorStyle}
            className={classNames([
              'box-table__title',
              classes('text-align-center'),
              classColor(textColor),
            ])}
          >
            <TextComponent size={headingSizesEnum[headingStyle]}>
              {title}
            </TextComponent>
          </Box>
          {subtitle && (
            <Box
              style={colorStyle}
              className={classNames([
                'box-table__subtitle',
                classes('margin-top-1'),
                classes('text-align-center'),
                classes('bold'),
                classes('uppercase'),
              ])}
            >
              <Text size="small">{subtitle}</Text>
            </Box>
          )}
        </Box>
      </a>
    );

    const boxOutputWithTrk = (
      <Impression eventType="element_impression" entityName="box_table">
        <ElementImpression {...elementImpressionProps}>
          {boxOutput}
        </ElementImpression>
      </Impression>
    );

    const boxTableBoxOutput = shouldTrack ? boxOutputWithTrk : boxOutput;

    const boxWrapper = (
      <div
        className={classNames([
          `box-table__container-${type}`,
          linkUrl && `${hoverStyle}`,
          layout,
          classes('background-white'),
          classes(`${borderStyle}`),
        ])}
      >
        {boxTableBoxOutput}
      </div>
    );

    return boxWrapper;
  }
}
// eslint-disable-next-line react/static-property-placement -- directive added automatically by Shepherd migration
BoxTableBox.propTypes = {
  type: PropTypes.oneOf(['active', 'inactive']),
  linkUrl: PropTypes.string,
  highlight: PropTypes.string,
  title: PropTypes.string,
  headingStyle: PropTypes.string,
  textColor: PropTypes.string,
  subtitle: PropTypes.string,
  shadow: PropTypes.bool,
  newTab: PropTypes.bool,
  redirectUrl: PropTypes.string,
  use2021Layout: PropTypes.bool,
  textColorOptions: PropTypes.string,
  layout: PropTypes.string,
  shouldTrack: PropTypes.bool,
  iconUrl: PropTypes.string,
  children: PropTypes.node,
};

// eslint-disable-next-line react/static-property-placement -- directive added automatically by Shepherd migration
BoxTableBox.defaultProps = {
  type: 'active',
  title: 'Title goes here',
  headingStyle: '3',
  textColor: 'neutral-darkest',
  shadow: false,
  newTab: false,
  use2021Layout: false,
  iconUrl: '',
  children: null,
};

BoxTableBox.getEditingConfig = (componentDocument) => {
  const spec = [
    stringInputSpec('title', 'Heading'),
    stringSelectSpec('headingStyle', 'Heading Style', [
      { label: 'Select one', value: '' },
      { label: 'Heading 1', value: '1' },
      { label: 'Heading 2', value: '2' },
      { label: 'Heading 3', value: '3' },
      { label: 'Heading 4', value: '4' },
      { label: 'Heading 5', value: '5' },
    ]),
    stringInputSpec('subtitle', 'Subtitle'),
    ...getTextColor.getEditingConfig({ props: componentDocument }),
    stringInputSpec('highlight', 'Highlighted Word'),
    stringInputSpec('linkUrl', 'Link URL'),
    stringInputSpec('iconUrl', 'Icon URL'),
    stringSelectSpec(
      'type',
      'Active / Inactive',
      [
        {
          value: 'active',
          label: 'active',
        },
        {
          value: 'inactive',
          label: 'inactive',
        },
      ],
      {
        help: 'Select active or inactive box type',
      }
    ),
    boolCheckboxSpec('shadow', 'Add box shadow?'),
    boolCheckboxSpec('newTab', 'Open in new tab?'),
  ];

  if (componentDocument && componentDocument.newTab)
    spec.push(
      stringInputSpec('redirectUrl', 'Redirect page after new tab opened?', {
        help: 'Link for page to redirect to after new tab is opened.',
      })
    );

  return spec;
};

// eslint-disable-next-line react/static-property-placement -- directive added automatically by Shepherd migration
BoxTableBox.displayName = 'BoxTableBox';

export default BoxTableBox;
